import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Wind, TrendingUp, AlertTriangle, User, Loader2, Activity, FileDown } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

const TEST_LABELS = { YOYO: 'Yo-Yo', GACON: 'Gacon' };

export default function AthleteAerobicProfilePage() {
  const { athleteId } = useParams();
  const { api } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const t = (en, it) => language === 'IT' ? it : en;

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const handleExportAerobicReport = async () => {
    setExporting(true);
    try {
      const res = await api.post('/premium-reports/aerobic',
        { athlete_id: athleteId }, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `aerobic_report_${athleteId.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Aerobic report export failed', e);
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    api.get(`/aerobic/athlete/${athleteId}/profile`).then(r => setProfile(r.data))
      .catch(() => navigate('/staff/aerobic')).finally(() => setLoading(false));
  }, [api, athleteId, navigate]);

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-zinc-500" /></div>;
  if (!profile) return null;

  const { athlete, latest, best_ever, history, total_tests, alerts } = profile;

  const chartData = {};
  for (const [tt, items] of Object.entries(history)) {
    chartData[tt] = items.filter(r => r.date).sort((a, b) => a.date.localeCompare(b.date)).map(r => ({
      date: r.date, mas: r.mas_vam_kmh, distance: r.total_distance_m, hr: r.heart_rate_bpm,
    }));
  }

  return (
    <div className="space-y-6 fade-in" data-testid="athlete-aerobic-profile">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="text-zinc-400 hover:text-zinc-100">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-zinc-100 flex items-center gap-2">
            <User className="w-5 h-5 text-teal-400" /> {athlete.name}
          </h1>
          <p className="text-sm text-zinc-500">{athlete.position} • {t('Aerobic Profile', 'Profilo Aerobico')}</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleExportAerobicReport}
          disabled={exporting}
          className="border-teal-500/40 text-teal-100 bg-teal-950/30 hover:bg-teal-900/40"
          data-testid="export-aerobic-report-btn"
        >
          {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
          {t('Export Aerobic Report', 'Esporta Report Aerobico')}
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-zinc-900/50 border-zinc-800"><CardContent className="p-4 text-center">
          <Activity className="w-5 h-5 text-teal-400 mx-auto mb-1" />
          <div className="text-2xl font-bold text-zinc-100" data-testid="total-tests">{total_tests}</div>
          <div className="text-xs text-zinc-500">{t('Total Tests', 'Test Totali')}</div>
        </CardContent></Card>
        <Card className="bg-zinc-900/50 border-zinc-800"><CardContent className="p-4 text-center">
          <Wind className="w-5 h-5 text-emerald-400 mx-auto mb-1" />
          <div className="text-2xl font-bold text-zinc-100" data-testid="test-types">{Object.keys(history).length}</div>
          <div className="text-xs text-zinc-500">{t('Test Types', 'Tipi')}</div>
        </CardContent></Card>
        <Card className="bg-zinc-900/50 border-zinc-800"><CardContent className="p-4 text-center">
          <TrendingUp className="w-5 h-5 text-teal-400 mx-auto mb-1" />
          <div className="text-2xl font-bold text-teal-400" data-testid="best-mas">
            {best_ever.YOYO?.mas_vam_kmh || best_ever.GACON?.mas_vam_kmh || '—'}
          </div>
          <div className="text-xs text-zinc-500">{t('Best MAS', 'MAS Migliore')} (km/h)</div>
        </CardContent></Card>
        <Card className="bg-zinc-900/50 border-zinc-800"><CardContent className="p-4 text-center">
          <AlertTriangle className="w-5 h-5 text-red-400 mx-auto mb-1" />
          <div className="text-2xl font-bold text-zinc-100" data-testid="alert-count">{alerts.length}</div>
          <div className="text-xs text-zinc-500">{t('Alerts', 'Allerte')}</div>
        </CardContent></Card>
      </div>

      {alerts.length > 0 && (
        <Card className="bg-red-500/5 border-red-500/30">
          <CardHeader className="pb-2"><CardTitle className="text-red-400 text-sm flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> {t('Active Alerts', 'Allerte')}</CardTitle></CardHeader>
          <CardContent className="space-y-1">
            {alerts.map((a, i) => (
              <div key={i} className="text-sm flex justify-between p-2 bg-red-500/5 rounded-sm">
                <span className="text-zinc-300">{a.type.replace('_', ' ')}</span>
                <span className="text-red-400 text-xs">
                  {a.drop_pct ? `${a.drop_pct}%` : ''}{a.below_pct ? `${a.below_pct}% below avg` : ''}
                  {a.session_date && ` • ${a.session_date}`}
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Latest Results */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader className="pb-2"><CardTitle className="text-zinc-100 text-base">{t('Latest Results', 'Ultimi')}</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {Object.entries(latest).filter(([_, d]) => d).map(([tt, d]) => (
              <div key={tt} className="p-3 rounded-sm border border-zinc-800 bg-zinc-950/50">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-semibold text-teal-400">{TEST_LABELS[tt]} {d.protocol_name ? `(${d.protocol_name})` : ''}</span>
                  <span className="text-xs text-zinc-500">{d.date}</span>
                </div>
                <div className="text-xl font-bold text-zinc-100">{d.mas_vam_kmh} km/h <span className="text-xs text-zinc-500 font-normal">MAS/VAM</span></div>
                <div className="flex gap-4 mt-1 text-xs text-zinc-400">
                  <span>{d.total_distance_m}m</span>
                  <span>{d.final_speed_kmh} km/h</span>
                  {d.heart_rate_bpm && <span>{d.heart_rate_bpm} bpm</span>}
                  {d.level_reached && <span>Lv.{d.level_reached}</span>}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Trend Charts */}
      {Object.entries(chartData).filter(([_, d]) => d.length > 1).map(([tt, data]) => (
        <Card key={tt} className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-zinc-100 text-base">{TEST_LABELS[tt]} — MAS/VAM {t('Trend', 'Andamento')}</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={data}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis dataKey="date" tick={{ fill: '#888', fontSize: 11 }} />
                <YAxis tick={{ fill: '#888', fontSize: 11 }} domain={['auto', 'auto']} />
                <Tooltip contentStyle={{ backgroundColor: '#1c1c1c', border: '1px solid #333' }} />
                <Line type="monotone" dataKey="mas" stroke="#2dd4bf" strokeWidth={2} dot={{ fill: '#2dd4bf', r: 4 }} name="MAS (km/h)" />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      ))}

      {/* Full History */}
      {Object.entries(history).map(([tt, items]) => (
        <Card key={tt} className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="pb-2"><CardTitle className="text-zinc-100 text-base">{TEST_LABELS[tt]} — {t('History', 'Storico')}</CardTitle></CardHeader>
          <CardContent className="space-y-1.5">
            {items.map(r => (
              <div key={r.id} className="flex items-center justify-between p-2 rounded-sm border border-zinc-800 bg-zinc-950/30">
                <span className="text-sm text-zinc-200">{r.date || '—'} {r.protocol_name ? `(${r.protocol_name})` : ''}</span>
                <div className="flex items-center gap-3 text-sm">
                  <span className="text-teal-400 font-semibold">{r.mas_vam_kmh} km/h</span>
                  <span className="text-zinc-400">{r.total_distance_m}m</span>
                  {r.heart_rate_bpm && <span className="text-zinc-500 text-xs">{r.heart_rate_bpm} bpm</span>}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
